import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
  Optional,
} from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { GtwSnackbarService } from '../../../../shared/_services/gtw-snackbar.service';
import { NotificationService } from '../notification.service';

import { safeJsonInput } from '../../../../shared/_utils/safe-json-input';
export interface NotificationPayloadModalData {
  processName: string;
  notificationId: string | number;
  payloadRaw: any;
}

/**
 * Migrated payload-detail modal (legacy: process-console-details.html / payloadDetail controller).
 *
 * Responsibilities:
 * - Pretty-print the JSON payload that came from the Process Console grid row
 * - Allow the user to resend the original notification by id
 */
@Component({
    selector: 'gtw-notification-payload-modal',
    templateUrl: './notification-payload-modal.component.html',
    styleUrls: ['./notification-payload-modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class NotificationPayloadModalComponent implements OnInit, OnDestroy {
  title = '';
  notificationId: string | number = '';
  payloadView = '';
  minimise = false;
  resending = false;

  private readonly destroy$ = new Subject<void>();

  constructor(
    @Optional() private dialogRef: MatDialogRef<NotificationPayloadModalComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) private data: NotificationPayloadModalData,
    private notificationService: NotificationService,
    private snackbar: GtwSnackbarService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.title = this.data?.processName || 'Notification Payload';
    this.notificationId = this.data?.notificationId ?? '';
    this.payloadView = this.toPrettyPayload(this.data?.payloadRaw);
    this.cdr.detectChanges();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  toggleMinimise(): void {
    this.minimise = !this.minimise;
    this.cdr.detectChanges();
  }

  cancel(): void {
    this.dialogRef?.close();
  }

  resendNotification(): void {
    if (!this.notificationId) {
      this.snackbar.openSnackBar('Notification id is required to resend', 'gtw-snackbar--yellow');
      return;
    }
    this.resending = true;
    this.cdr.detectChanges();

    this.notificationService.resendNotification(this.notificationId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.resending = false;
          if (String(res?.callSuccess) === '1') {
            this.snackbar.openSnackBar('Resent the email successfully', 'gtw-snackbar--green');
          } else {
            this.snackbar.openSnackBar(
              res?.errorMessage || 'An error has occurred. If this error continues please contact support.',
              'gtw-snackbar--red',
            );
          }
          this.cdr.detectChanges();
        },
        error: () => {
          this.resending = false;
          this.snackbar.openSnackBar(
            'An error has occurred. If this error continues please contact support.',
            'gtw-snackbar--red',
          );
          this.cdr.detectChanges();
        },
      });
  }

  private toPrettyPayload(payload: any): string {
    if (payload === null || payload === undefined || payload === '') return '';
    if (typeof payload === 'object') {
      try { return JSON.stringify(payload, null, 2); } catch { return String(payload); }
    }
    const asString = String(payload);
    try {
      const parsed = safeJsonInput<any>(asString, null, 'notification-payload-modal.parse1');
      return JSON.stringify(parsed, null, 2);
    } catch {
      return asString;
    }
  }
}
